/**
 * SendSummary Component
 *
 * Summary card with recipient count and send/schedule action buttons.
 */

import { Send, Clock, Users, Zap, Loader2 } from "lucide-react";
import { Button } from "../../../shared/components/ui/Button";
import type { SegmentSummary } from "../types";

interface SendSummaryProps {
  selectedSegment: SegmentSummary | null;
  isValid: boolean;
  sending: boolean;
  onSend: () => void;
  onSchedule: () => void;
}

export function SendSummary({
  selectedSegment,
  isValid,
  sending,
  onSend,
  onSchedule,
}: SendSummaryProps) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
      <div className="flex items-center gap-2 mb-5">
        <Send size={18} className="text-violet-500" />
        <h3 className="font-semibold text-slate-900">Resumen de Envío</h3>
      </div>

      {/* Summary Stats */}
      <div className="space-y-3 mb-6">
        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Users size={15} className="text-slate-400" />
            <span>Destinatarios</span>
          </div>
          <span className="text-sm font-bold text-slate-900">
            {selectedSegment
              ? selectedSegment.lastKnownCount.toLocaleString()
              : "—"}
          </span>
        </div>

        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Zap size={15} className="text-slate-400" />
            <span>Canal</span>
          </div>
          <span className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
            <span className="w-2 h-2 bg-green-400 rounded-full"></span>
            Notificación push
          </span>
        </div>

        {selectedSegment && (
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Users size={15} className="text-slate-400" />
              <span>Segmento</span>
            </div>
            <span className="text-sm font-semibold text-violet-600 truncate max-w-[140px]">
              {selectedSegment.name}
            </span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        <button
          onClick={onSend}
          disabled={!isValid || sending}
          className={`
                        w-full py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-300
                        ${
                          isValid && !sending
                            ? "bg-violet-600 text-white shadow-lg shadow-violet-500/25 hover:bg-violet-700 hover:shadow-violet-500/40 active:scale-[0.98]"
                            : "bg-slate-100 text-slate-400 cursor-not-allowed"
                        }
                    `}
        >
          {sending ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Enviando...
            </>
          ) : (
            <>
              <Send size={16} />
              Enviar Notificación
            </>
          )}
        </button>

        {/* Audit C-12: la programación todavía no existe — la mostramos
            deshabilitada con badge "Pronto" para no prometer un camino muerto. */}
        <Button
          variant="secondary"
          size="lg"
          onClick={onSchedule}
          disabled
          title="Disponible próximamente"
          className="w-full opacity-70 cursor-not-allowed"
        >
          <Clock size={16} />
          Programar envío
          <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-500">
            Pronto
          </span>
        </Button>
      </div>

      {/* Validation hint */}
      {!isValid && (
        <p className="text-xs text-slate-400 text-center mt-3">
          Elegí segmento, título y cuerpo para habilitar el envío
        </p>
      )}
    </div>
  );
}

export default SendSummary;
